import { useCallback, useEffect, useState } from 'react';
import { Confirm, Profile, type Destructive, SignIn } from '@drbell/ui';
import { api, type Access, type Me, type PatientSummary } from './api.ts';
import { Timeline } from './screens/Timeline.tsx';
import { Report } from './screens/Report.tsx';
import { Invitations } from './screens/Invitations.tsx';
import { Medicines } from './screens/Medicines.tsx';
import { Appointments } from './screens/Appointments.tsx';
import { People } from './screens/People.tsx';
import { InvitePage, inviteTokenFromPath, takeInviteReturn } from './screens/InvitePage.tsx';
import { Statement } from './screens/Statement.tsx';
import { Progress } from './screens/Progress.tsx';
import { Measurements } from './screens/Measurements.tsx';
import { Caring } from './screens/Caring.tsx';
import { PartnerPortal, isPartnerPath, takePartnersReturn } from './screens/PartnerPortal.tsx';
import { SetUp } from './screens/SetUp.tsx';
import { Routine } from './screens/Routine.tsx';
import { RecordSettings } from './screens/RecordSettings.tsx';
import { Plan } from './screens/Plan.tsx';
import { Feedback } from './screens/Feedback.tsx';
import { GetTheApp, RingingHere } from './screens/GetTheApp.tsx';
import { orderPeople, setupChoices } from './self-service.ts';
import { BillingScreen, isPlansPath } from './screens/Billing.tsx';

/** `statement` is reached from History, not the tab bar. */
type Tab = 'today' | 'medicines' | 'measurements' | 'routine' | 'appointments' | 'people' | 'settings' | 'progress' | 'history' | 'statement';
import { AnnouncementBar, Mark, ReportWrongWordLink, ThemeToggle, useBrandAccent } from '@drbell/ui';

/**
 * DrBell on the web — care.drbell.app. RX-CARE-14, and since 4 October 2026
 * a home for a person's own reminders too.
 *
 * Deliberately one screen deep. The person opening this has a specific
 * question — has she taken her tablets, or what is due for me — and every
 * level of navigation between them and the answer is a level they have to
 * think about at a moment when they are already worried.
 *
 * Their own record comes first ("You"), then the people they help. Somebody
 * with no record yet sets one up here — their own, or for someone they look
 * after — rather than being sent to the phone (SetUp.tsx). The reminders
 * still ring on a phone, and every set-up says so.
 */
export function App() {
  const [me, setMe] = useState<Me | null | 'loading'>('loading');
  const [patientId, setPatientId] = useState<string | null>(null);
  const [pending, setPending] = useState<Destructive | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  /** "Send feedback": the phone's "Tell us what to improve", into the same inbox. */
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('today');
  const [access, setAccess] = useState<Access | null>(null);
  const [accountStatement, setAccountStatement] = useState(false);
  /** Adding a record: their own, or someone else's (SetUp.tsx). */
  const [adding, setAdding] = useState<'choose' | 'helping' | null>(null);
  /** The control plane's flags: a screen look switched off there is not offered. */
  const [flags, setFlags] = useState<Record<string, unknown>>({});
  /** How I'm told: open, and which record to put first (from the People tab). */
  const [caring, setCaring] = useState<{ focus?: string } | null>(null);
  /*
   * The one other address this app answers: /i/<token>, the invitation link
   * an inviter sends from their own phone. The Worker serves this page for
   * any path, so the route is decided here.
   */
  const [inviteToken, setInviteToken] = useState<string | null>(() => inviteTokenFromPath(location.pathname));
  /*
   * And /partners: the partner portal (docs/26), a route area of this site
   * until partners.drbell.app points here.
   */
  const [partners, setPartners] = useState(() => isPartnerPath(location.pathname));

  const loadSession = useCallback(() => {
    api
      .get<Me>('/api/me')
      .then((m) => {
        setMe(m.user ? m : null);
        // Their own record first, when nothing was chosen; a record that went away is not kept.
        setPatientId((current) =>
          current && m.patients.some((p) => p.id === current) ? current : orderPeople(m.patients)[0]?.id ?? null);
        /*
         * Back from Google or Apple, which return to `/` rather than to the
         * invitation the sign-in was started from (see InvitePage).
         */
        // Back from Google or Apple to `/`, having started at /partners.
        if (m.user && !isPartnerPath(location.pathname) && takePartnersReturn()) {
          history.replaceState(null, '', '/partners');
          setPartners(true);
        }
        if (m.user && !inviteTokenFromPath(location.pathname)) {
          const token = takeInviteReturn();
          if (token) {
            history.replaceState(null, '', `/i/${encodeURIComponent(token)}`);
            setInviteToken(token);
          }
        }
      })
      // A 401 is the normal signed-out case, not an error worth showing.
      .catch(() => setMe(null));
  }, []);

  useEffect(loadSession, [loadSession]);

  const signedIn = me !== null && me !== 'loading';
  useEffect(() => {
    if (!signedIn) return;
    api
      .get<{ flags: Record<string, unknown> }>('/api/config?platform=web&surface=care')
      .then((r) => setFlags(r.flags ?? {}))
      // Unreachable flags are "on": losing a screen choice for want of a signal is the wrong way round.
      .catch(() => setFlags({}));
  }, [signedIn]);

  /*
   * What this person may do on the record in view. The server enforces it on
   * every call; this only decides which buttons to offer, so a viewer is not
   * shown "Stop…" and then refused.
   */
  const shownId = me && me !== 'loading'
    ? (me.patients.find((p) => p.id === patientId) ?? orderPeople(me.patients)[0])?.id
    : undefined;
  /** The home looking after this person, if any — RX-ORG-11. */
  const [home, setHome] = useState<{
    name: string; tagline: string | null; logoUrl: string | null;
    primaryColor: string | null; primaryColorDark: string | null;
  } | null>(null);
  useEffect(() => {
    setHome(null);
    if (!shownId) return;
    api
      .get<{ organisation: {
        name: string; tagline: string | null; logoUrl: string | null;
        primaryColor: string | null; primaryColorDark: string | null;
      } | null }>(
        `/api/patients/${shownId}/branding`,
      )
      .then((r) => setHome(r.organisation))
      .catch(() => setHome(null));
  }, [shownId]);
  useBrandAccent(home?.primaryColor, home?.primaryColorDark);

  useEffect(() => {
    setAccess(null);
    if (!shownId) return;
    api
      .get<{ access: Access }>(`/api/patients/${shownId}`)
      .then((r) => setAccess(r.access))
      .catch(() => setAccess(null));
  }, [shownId]);

  if (me === 'loading') return <p className="p-8 text-(--muted)">Checking session…</p>;

  if (partners) return <PartnerPortal signedIn={me !== null} onSignedIn={loadSession} />;
  // /plans: buying and managing a plan on the website (docs/32).
  if (isPlansPath(location.pathname)) return <BillingScreen signedIn={me !== null} onSignedIn={loadSession} />;

  if (inviteToken) {
    return (
      <InvitePage
        token={inviteToken}
        me={me}
        onSignedIn={loadSession}
        onDone={(joined) => {
          history.replaceState(null, '', '/');
          setInviteToken(null);
          if (joined) setPatientId(joined);
          setTab('today');
          loadSession();
        }}
      />
    );
  }

  if (!me) return <SignIn
        title="Your medicine reminders, and the people you look after"
        subtitle="Set up your own reminders, or check on someone you look after and be told when a dose is missed."
        onSignedIn={loadSession}
      />;

  /** Into a record just made, or just joined. */
  const open = (id: string, at: Tab = 'today') => {
    setPatientId(id);
    setTab(at);
    setAdding(null);
    loadSession();
  };

  if (me.patients.length === 0) {
    return <Welcome me={me} flags={flags} onAccepted={loadSession} onCreated={open} />;
  }

  const people = orderPeople(me.patients);
  const patient: PatientSummary = me.patients.find((p) => p.id === patientId) ?? people[0]!;
  const can = (capability: string) => access?.capabilities.includes(capability) ?? false;

  /*
   * Only the tabs this person can open. A viewer may see what is due but not
   * the medicines themselves, and staff do not read history; offering those
   * tabs only to answer them with a refusal is the "decorative button" this
   * repo keeps removing. Until access has loaded, everything is offered.
   */
  const TABS: ReadonlyArray<readonly [Tab, string, string | null]> = [
    ['today', 'Today', null],
    ['medicines', 'Medicines', 'medication.read'],
    // docs/33 D1: only while the admin console has measurements on.
    ...(flags['measurements.enabled'] === true ? [['measurements', 'Measurements', 'history.read'] as const] : []),
    // Everyone who can see the record reads its routine; changing it is checked inside.
    ['routine', 'Routine', null],
    ['appointments', 'Appointments', 'medication.read'],
    ['people', patient.isSelf ? 'Who looks after you' : 'Who looks after them', null],
    ['settings', 'Settings', null],
    // Parity with the phone (docs/33 C1): the week and the month, described, never graded.
    ['progress', 'Progress', 'history.read'],
    ['history', 'History', 'history.read'],
  ];
  const visibleTabs = TABS.filter(([, , needs]) => !needs || !access || can(needs)).map(
    ([id, label]) => [id, label] as const,
  );
  const shownTab: Tab =
    tab === 'statement' || visibleTabs.some(([id]) => id === tab) ? tab : 'today';

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      {/* A line from the DrBell team, in the reader's language (docs/08). */}
      <AnnouncementBar surface="care" get={api.get} />
      <header className="mb-6 border-b border-(--line) pb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Mark className="h-9 w-9 shrink-0" />

          {/*
            Sign out — the same gap as the organisation console had.
            
            This screen shows a named person's medication history and the
            session lasts ninety days. It is opened on family tablets, on work
            computers and on a sister's phone, and the only way to end a
            session was to clear the browser's cookies. The button existed on
            the empty state and nowhere a signed-in caregiver could reach it.
          */}
          <ThemeToggle className="ml-auto" />

          <button
            onClick={() => setProfileOpen(true)}
            className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
          >
            Your account
          </button>

          {/* The account's own plan: what it has, from where, until when. */}
          <button
            onClick={() => setPlanOpen(true)}
            className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
          >
            Your plan
          </button>

          {/* Beside the account rather than inside it: that dialog is shared
              by every console, and this statement is the caregiver's. */}
          <button
            onClick={() => setAccountStatement(true)}
            className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
          >
            Your statement
          </button>

          {/* About how DrBell reaches this person, for everyone they help, so
              it sits with the account and the statement, not in a record. */}
          <button
            onClick={() => setCaring({})}
            className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
          >
            How I&rsquo;m told
          </button>

          {/* What to improve, into the console's Feedback inbox (POST /api/feedback). */}
          <button
            onClick={() => setFeedbackOpen(true)}
            className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
          >
            Send feedback
          </button>

          <button
            onClick={() =>
              setPending({
                title: 'Sign out of DrBell on this device?',
                body: 'You will stop seeing what was taken until you sign in again. Reminders keep ringing on the phone either way \u2014 signing out here changes nothing for them.',
                confirmLabel: 'Sign out',
                onConfirm: async () => {
                  await api.post('/api/auth/sign-out');
                  location.reload();
                },
              })
            }
            className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
          >
            Sign out
          </button>
        </div>
      </header>

      <Invitations onAccepted={(joined) => { if (joined) setPatientId(joined); loadSession(); }} />

      {/*
        Whose record: their own first ("You"), then the people they help,
        each one press away — a select hid that there was anybody else. The
        last button sets up another: their own reminders when they have none
        yet, otherwise somebody else's (the phone's "+ Someone else").
      */}
      <nav className="mb-5 flex flex-wrap items-center gap-2" aria-label="People">
        {people.map((p) => {
          const shown = !adding && p.id === patient.id;
          return (
            <button
              key={p.id}
              onClick={() => { setPatientId(p.id); setAdding(null); }}
              aria-current={shown ? 'page' : undefined}
              className={`min-h-11 rounded-full border-2 px-4 text-sm ${
                shown ? 'border-(--accent) bg-(--accent-soft) font-medium text-(--accent)' : 'border-(--line) hover:border-(--accent)'
              }`}
            >
              {p.isSelf ? <>You <span className="text-(--muted)">· {p.displayName}</span></> : p.displayName}
            </button>
          );
        })}
        <button
          onClick={() => setAdding(setupChoices(me.patients).includes('me') ? 'choose' : 'helping')}
          aria-current={adding ? 'page' : undefined}
          className={`min-h-11 rounded-full border-2 border-dashed px-4 text-sm ${
            adding ? 'border-(--accent) text-(--accent)' : 'border-(--line) text-(--muted) hover:border-(--accent) hover:text-(--fg)'
          }`}
        >
          {setupChoices(me.patients).includes('me') ? '+ Set up my own reminders' : '+ Someone else'}
        </button>
      </nav>

      {adding ? (
        <SetUp
          key={adding}
          user={me.user!}
          patients={me.patients}
          start={adding}
          flags={flags}
          onCancel={() => setAdding(null)}
          onCreated={open}
        />
      ) : (
      <>
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          {patient.isSelf ? 'Your reminders' : patient.displayName}
        </h1>
        {home && (
          <p className="flex items-center gap-2 text-sm">
            {home.logoUrl && <img src={home.logoUrl} alt="" className="h-6 max-w-24 object-contain" />}
            <span>Cared for at {home.name}{home.tagline ? ` · ${home.tagline}` : ''}</span>
          </p>
        )}
        <p className="text-sm text-(--muted)">
          {/* Their timezone, said out loud, because a caregiver abroad
              needs to know whose clock these times are on. */}
          Times shown in {patient.timezone.replace(/_/g, ' ')}
        </p>
      </div>

      {/*
        Tabs — RX-CARE-14.

        Today stays first and is where the app opens: the question is still
        "has she taken her tablets". The rest is what a caregiver could only
        do from the phone until now — and, for somebody's own record, what
        they set up on the phone: the routine and the record's settings.
      */}
      <nav className="mb-6 flex flex-wrap gap-1" aria-label="Sections">
        {visibleTabs.map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            aria-current={shownTab === id ? 'page' : undefined}
            className={`min-h-11 rounded-lg px-3 text-sm ${
              shownTab === id ? 'bg-(--accent-soft) font-medium text-(--accent)' : 'hover:bg-(--panel)'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {/* Keyed by person: switching kept the last person's doses, error and
          busy state on screen under the new person's name until the new load
          landed. B2C audit. */}
      <div key={patient.id}>
      {shownTab === 'today' && (
        <>
          {/* A record that rings on this reader's own phones, with none yet: the app is the next step. */}
          <RingingHere patientId={patient.id} how={me.user?.phoneNumber} />
          <Timeline patient={patient} userId={me.user!.id} canAcknowledge={can('dose.acknowledge')} />
          {(!access || can('history.read')) && <button
            onClick={() => setTab('history')}
            className="mt-6 min-h-11 w-full rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)"
          >
            History and what to show the doctor
          </button>}
        </>
      )}
      {shownTab === 'medicines' && (
        <Medicines patient={patient} canEdit={can('medication.edit')} />
      )}
      {shownTab === 'routine' && (
        <Routine patient={patient} onOpenMedicines={() => setTab('medicines')} />
      )}
      {shownTab === 'appointments' && (
        <Appointments patient={patient} canEdit={can('medication.edit')} />
      )}
      {shownTab === 'settings' && (
        <RecordSettings
          patient={patient}
          flags={flags}
          onChanged={loadSession}
          onErased={() => { setPatientId(null); setTab('today'); loadSession(); }}
        />
      )}
      {shownTab === 'people' && (
        <People
          patient={patient}
          canManage={can('grant.manage')}
          userId={me.user!.id}
          onChanged={loadSession}
          onHowTold={() => setCaring({ focus: patient.id })}
        />
      )}
      {shownTab === 'measurements' && (
        <Measurements patient={patient} canRecord={can('dose.acknowledge')} canManage={can('medication.edit')} />
      )}
      {shownTab === 'progress' && <Progress patient={patient} onHistory={() => setTab('history')} />}
      {shownTab === 'history' && (
        <Report patient={patient} onBack={() => setTab('today')} onStatement={() => setTab('statement')} />
      )}
      {shownTab === 'statement' && (
        <Statement scope={{ kind: 'record', patient }} onBack={() => setTab('history')} />
      )}
      </div>
      </>
      )}

      <footer className="mt-10 flex flex-wrap items-baseline justify-between gap-3 border-t border-(--line) pt-4 text-xs text-(--muted)">
        <span>
          DrBell reminds and records. It does not give medical advice.
          Always follow the prescribing doctor.
        </span>
        {/* Only in a language other than English (docs/31). */}
        <ReportWrongWordLink api={api} locale={me.user?.locale} source="care" />
      </footer>

      {/* A dialog like the account itself: it is about this person, not about
          whoever's record is on screen behind it. */}
      {accountStatement && (
        <div
          className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Your statement"
        >
          <div className="my-8 w-full min-w-0 max-w-2xl rounded-2xl border border-(--line) bg-(--bg) p-4 sm:p-5">
            <Statement scope={{ kind: 'account' }} onBack={() => setAccountStatement(false)} />
          </div>
        </div>
      )}

      {caring && (
        <div
          className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="How I'm told"
        >
          <div className="my-8 w-full min-w-0 max-w-2xl rounded-2xl border border-(--line) bg-(--bg) p-4 sm:p-5">
            <Caring
              focus={caring.focus}
              onBack={() => setCaring(null)}
              onLeft={(left, remaining) => {
                /*
                 * Their access ended at once, so the record goes from the
                 * switcher. If it was the one on screen, the next one takes
                 * its place; with nobody left, the empty state does, and this
                 * dialog has nothing more to say.
                 */
                if (left === patient.id) setPatientId(null);
                if (remaining === 0) setCaring(null);
                loadSession();
              }}
            />
          </div>
        </div>
      )}

      {profileOpen && (
        <Profile api={api} onClose={() => setProfileOpen(false)} onSaved={loadSession} />
      )}

      {planOpen && (
        <div
          className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Your plan"
        >
          <div className="my-8 w-full min-w-0 max-w-xl rounded-2xl border border-(--line) bg-(--bg) p-4 sm:p-5">
            <Plan onClose={() => setPlanOpen(false)} />
          </div>
        </div>
      )}

      {feedbackOpen && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Send feedback">
          <div className="my-8 w-full min-w-0 max-w-xl rounded-2xl border border-(--line) bg-(--bg) p-4 sm:p-5">
            <Feedback onClose={() => setFeedbackOpen(false)} />
          </div>
        </div>
      )}

      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </div>
  );
}

/**
 * Signed in, with no record yet — the dead end this used to be.
 *
 * It said "Nobody to show yet… use the DrBell app on your phone", so
 * somebody who signed up here could do nothing at all. Three different
 * people land here, and each gets their way on: an invitation waiting
 * (shown first, to approve or reject); somebody setting up their own
 * reminders; somebody helping someone else (SetUp.tsx explains invitations,
 * and sets up a record for a person with no phone). And the truth: the
 * reminders ring on a phone.
 */
function Welcome({ me, flags, onAccepted, onCreated }: {
  me: Me;
  flags: Record<string, unknown>;
  onAccepted: (patientId?: string) => void;
  onCreated: (patientId: string, tab: 'today' | 'medicines') => void;
}) {
  const [planOpen, setPlanOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const who = me.user?.phoneNumber ?? null;
  const button = 'min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)';
  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8">
      <AnnouncementBar surface="care" get={api.get} />
      <header className="mb-6 flex flex-wrap items-center gap-2 border-b border-(--line) pb-4">
        <Mark className="h-9 w-9 shrink-0" />
        <ThemeToggle className="ml-auto" />
        <button onClick={() => setProfileOpen(true)} className={button}>Your account</button>
        <button onClick={() => setPlanOpen(true)} className={button}>Your plan</button>
        <button onClick={() => setFeedbackOpen(true)} className={button}>Send feedback</button>
        <button onClick={() => { void api.post('/api/auth/sign-out').then(() => location.reload()); }} className={button}>
          Sign out
        </button>
      </header>

      <h1 className="text-2xl font-semibold tracking-tight">Welcome to DrBell</h1>
      <p className="mb-5 mt-1 text-sm text-(--muted)">
        {who ? <>{who} is signed in. </> : null}
        Invitations to {who ? 'this number' : 'you'} appear here as soon as they are sent.
      </p>

      <Invitations onAccepted={onAccepted} />

      <SetUp user={me.user!} patients={me.patients} flags={flags} onCreated={onCreated} appCard={false} />

      <div className="mt-6">
        <GetTheApp how={who} />
      </div>

      {planOpen && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Your plan">
          <div className="my-8 w-full min-w-0 max-w-xl rounded-2xl border border-(--line) bg-(--bg) p-4 sm:p-5">
            <Plan onClose={() => setPlanOpen(false)} />
          </div>
        </div>
      )}
      {profileOpen && <Profile api={api} onClose={() => setProfileOpen(false)} onSaved={() => onAccepted()} />}
      {feedbackOpen && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Send feedback">
          <div className="my-8 w-full min-w-0 max-w-xl rounded-2xl border border-(--line) bg-(--bg) p-4 sm:p-5">
            <Feedback onClose={() => setFeedbackOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

